import { createAction, props } from '@ngrx/store';

import { NotificationItem } from '@shared/models/in-app-notification.model';
import { PaginationResponse } from '@shared/models/common.model';

/** API max page size for `GET /api/notifications`. */
export const NOTIFICATIONS_PAGE_SIZE = 20;

/**
 * Append a new entry to the bell inbox. The reducer takes care of de-dup
 * (by `item.id` and by `correlationId`+`type`).
 */
export const notificationReceived = createAction(
  '[Notifications] Received',
  props<{ item: NotificationItem }>()
);

/**
 * Load a page of in-app notifications from notification-management.
 * Initial hydrate uses `page: 1, append: false`; infinite scroll uses
 * `append: true` with the next page.
 */
export const loadNotifications = createAction(
  '[Notifications] Load',
  props<{ page?: number; limit?: number; append?: boolean }>()
);

export const loadNotificationsSuccess = createAction(
  '[Notifications] Load Success',
  props<{
    items: NotificationItem[];
    pagination: PaginationResponse;
    append: boolean;
    /** Present on initial hydrate; omitted on append so unread stays as-is. */
    unreadCount?: number;
  }>()
);

export const loadNotificationsFailure = createAction(
  '[Notifications] Load Failure',
  props<{ error: unknown }>()
);

/**
 * Optimistic local mark-read. An effect calls `POST /api/notifications/read`
 * with the item's `notificationId` (from WS envelope or REST row).
 */
export const markNotificationRead = createAction(
  '[Notifications] Mark Read',
  props<{ id: string }>()
);

export const markNotificationReadFailure = createAction(
  '[Notifications] Mark Read Failure',
  props<{ id: string; error: unknown }>()
);

/**
 * Optimistic local mark-all. An effect persists via
 * `POST /api/notifications/read-all`.
 */
export const markAllNotificationsRead = createAction(
  '[Notifications] Mark All Read'
);

export const markAllNotificationsReadFailure = createAction(
  '[Notifications] Mark All Read Failure',
  props<{ error: unknown }>()
);

/** Session-only wipe of the in-memory list (no delete API). */
export const clearNotifications = createAction('[Notifications] Clear');
